<template>
    <custom-dialog v-model:visible="dialogVisible" header="Historial de Cambios" :style="dialogStyle">
        <div class="changelog-container">
            <div v-if="loading" class="loading-indicator">
                <span>Cargando...</span>
            </div>
            <div v-else class="changelog-content" v-html="formattedChangelog"></div>
        </div>
    </custom-dialog>
</template>

<script>
import CustomDialog from '@/components/ui/CustomDialog.vue';

export default {
    name: 'ChangelogDialog',
    components: {
        CustomDialog
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            changelogContent: '',
            loading: true,
            dialogStyle: {
                width: '600px',
                height: '500px',
                maxWidth: '90vw',
                maxHeight: '80vh',
                overflow: 'hidden',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
            }
        };
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
        formattedChangelog() {
            if (!this.changelogContent) return '';

            // Formateo básico de Markdown a HTML
            let html = this.changelogContent
                // Convertir encabezados
                .replace(/^# (.+)$/gm, '<h1>$1</h1>')
                .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                .replace(/^### (.+)$/gm, '<h3>$1</h3>')

                // Convertir listas
                .replace(/^- (.+)$/gm, '<li>$1</li>')
                .replace(/(<li>.*<\/li>\n?)+/gm, function (match) {
                    return '<ul>' + match + '</ul>';
                })

                // Convertir énfasis
                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                .replace(/\_(.+?)\_/g, '<em>$1</em>')

                // Convertir enlaces
                .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank">$1</a>')

                // Convertir párrafos (excepto los que ya son elementos HTML)
                .replace(/^(?!<[hl]|<ul|$)(.+)$/gm, '<p>$1</p>')

                // Horizontal rule
                .replace(/^---$/gm, '<hr>')

                // Conservar saltos de línea entre párrafos
                .replace(/\n\n/g, '<br>');

            return html;
        }
    },
    watch: {
        visible(newValue) {
            if (newValue && !this.changelogContent) {
                this.fetchChangelog();
            }
        }
    },
    methods: {
        async fetchChangelog() {
            this.loading = true;
            try {
                // Intentar cargar desde la carpeta public
                const possiblePaths = [
                    '/CHANGELOG.md',
                    '/assets/CHANGELOG.md',
                    `${process.env.BASE_URL}CHANGELOG.md`
                ];

                let loaded = false;

                // Intentar cargar desde rutas web
                for (const path of possiblePaths) {
                    try {
                        
                        const response = await fetch(path, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'text/plain',
                                'Cache-Control': 'no-cache'
                            }
                        });

                        if (response.ok) {
                            this.changelogContent = await response.text();
                            
                            loaded = true;
                            break;
                        } else {
                            
                        }
                    } catch (pathError) {
                        
                    }
                }

                // Si no se pudo cargar de ninguna ubicación, cargar el contenido directamente
                if (!loaded) {
                    // Contenido estático del archivo CHANGELOG
                  
                    loaded = true;
                }
            } catch (error) {
                
                this.changelogContent = `# Error al cargar el Historial de Cambios

Lo sentimos, ocurrió un error al intentar cargar el historial de cambios: ${error.message}`;
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style>
/* Quitamos el atributo "scoped" para evitar el problema con PostCSS */
.changelog-container {
    padding: 10px;
    font-family: var(--font-body);
    line-height: 1.6;
    overflow-y: auto;
    height: calc(100% - 20px);
    position: relative;
}

.loading-indicator {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    font-style: italic;
    color: var(--text-secondary-color);
}

.changelog-content {
    padding: 10px;
    position: relative;
    height: 100%;
    overflow-y: auto;
}

/* Usamos clases específicas con prefijo para evitar colisiones */
.changelog-content h1 {
    font-size: 1.8rem;
    margin-top: 0;
    color: var(--primary-color, #1976d2);
}

.changelog-content h2 {
    font-size: 1.4rem;
    border-bottom: 1px solid #ddd;
    padding-bottom: 10px;
    margin-top: 20px;
    color: var(--text-color, #333);
}

.changelog-content h3 {
    font-size: 1.2rem;
    color: var(--text-secondary-color, #666);
    margin-top: 15px;
}

.changelog-content ul {
    padding-left: 20px;
    margin-top: 10px;
    margin-bottom: 10px;
}

.changelog-content li {
    margin-bottom: 5px;
}

.changelog-content a {
    color: var(--primary-color, #1976d2);
    text-decoration: none;
}

.changelog-content a:hover {
    text-decoration: underline;
}

.changelog-content hr {
    border: none;
    border-top: 1px solid #ddd;
    margin: 20px 0;
}

.changelog-content strong {
    font-weight: 600;
}

/* Estilos adicionales para mejorar la visualización en diálogo modal */
:deep(.p-dialog-content) {
    overflow: hidden !important;
    padding: 0 !important;
    height: 100% !important;
    position: relative !important;
}

:deep(.p-dialog) {
    display: flex !important;
    flex-direction: column !important;
    margin: 0 !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
}

:deep(.p-dialog-header) {
    flex-shrink: 0 !important;
}

:deep(.p-dialog-footer) {
    flex-shrink: 0 !important;
}

:deep(.p-dialog-mask) {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 9000 !important;
    background-color: rgba(0, 0, 0, 0.4) !important;
}

/* Asegurar que el diálogo flote independientemente de su contenedor */
:deep(.p-component-overlay) {
    position: fixed !important;
}
</style>
